import { Button, Col, FormGroup, Label } from 'reactstrap';
import React, { FunctionComponent, useState } from 'react';
import { withApiData, ApiDataBinding } from 'react-api-data';
import { compose } from 'recompose';
import { withTranslation, WithTranslation } from 'react-i18next';
import { ReportsInterval, Roles } from 'constants/literals';

// components
import FormControl from 'components/utils/FormControl';
import RolesChecker from 'components/utils/RolesChecker';
import { CenteredAlert } from 'components/styled';
import { ApiResponse, ReportType, ReactApiDataActions } from 'constants/api';
import withUserRoles from 'hocs/withUserRoles';
import RolesSelect from 'components/utils/RolesSelect';
import FormModal from './FormModal';

interface InputProps {
    pageNumber: number;
    perPage: number;
    sortBy: string;
    sortOrder: string;
}
interface EnhanceProps {
    onSubmit: (values: any) => boolean;
    newReportRequest: ApiDataBinding<ApiResponse<{ reports: ReportType[] }>>;
    apiDataActions: ReactApiDataActions;
    userRoles: Roles[];
}

type Props = InputProps & EnhanceProps & WithTranslation;

const enhance = compose<Props, InputProps>(
    withApiData({
        newReportRequest: 'postNewReportRequest',
    }),
    withTranslation(),
    withUserRoles
);

type FormValues = Pick<ReportType, 'name' | 'description' | 'frequency' | 'cloud_drive_path' | 'roles'>;

const ReportAddModal: FunctionComponent<Props> = ({
    newReportRequest,
    apiDataActions,
    pageNumber,
    perPage,
    sortBy,
    sortOrder,
    t,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [userRoles, setUserRoles] = useState('');

    function toggle() {
        setIsOpen((isOpen) => !isOpen);
    }
    function onSubmit(values: FormValues) {
        return newReportRequest.perform({}, { report: { ...values, roles: userRoles } });
    }
    function onSucces() {
        apiDataActions.invalidateCache('getReports');
        apiDataActions.perform('getReports', {
            page: pageNumber,
            per_page: perPage,
            sort: sortBy,
            order: sortOrder,
        });
    }

    return (
        <React.Fragment>
            <FormModal
                header={t('reportsModals.addModalTitle')}
                isOpen={isOpen}
                toggle={toggle}
                initialValues={{
                    name: '',
                    description: '',
                    frequency: ReportsInterval.Monthly,
                    cloud_drive_path: '',
                    roles: '',
                }}
                onSubmit={onSubmit}
                onSuccess={onSucces}
                successMessage={t('notification.createReportSuccessful')}
            >
                {({ errors, touched }) => (
                    <React.Fragment>
                        <FormGroup row>
                            <Col md={3}>
                                <Label htmlFor="name">{t('reportsCommon.name')}</Label>
                            </Col>
                            <Col md={9}>
                                <FormControl name="name" errors={errors} touched={touched} required />
                            </Col>
                        </FormGroup>
                        <FormGroup row>
                            <Col md={3}>
                                <Label htmlFor="description">{t('reportsCommon.description')}</Label>
                            </Col>
                            <Col md={9}>
                                <FormControl name="description" errors={errors} touched={touched} />
                            </Col>
                        </FormGroup>
                        <FormGroup row>
                            <Col md="3">
                                <Label htmlFor="frequency">{t('reportsCommon.frequency')}</Label>
                            </Col>
                            <Col md="9">
                                <FormControl component="select" name="frequency" errors={errors} touched={touched}>
                                    {Object.values(ReportsInterval).map((period) => (
                                        <option key={period} value={period}>
                                            {period}
                                        </option>
                                    ))}
                                </FormControl>
                            </Col>
                        </FormGroup>
                        <FormGroup row>
                            <Col md={3}>
                                <Label htmlFor="cloud_drive_path">{t('reportsCommon.reportPath')}</Label>
                            </Col>
                            <Col md={9}>
                                <FormControl name="cloud_drive_path" errors={errors} touched={touched} required />
                            </Col>
                        </FormGroup>
                        <FormGroup row>
                            <Col md="3">
                                <Label htmlFor="roles">{t('reportsCommon.roles')}</Label>
                            </Col>
                            <Col md="9">
                                <RolesSelect
                                    onChange={(roles) => {
                                        setUserRoles(roles);
                                    }}
                                    existingRoles={''}
                                />
                            </Col>
                        </FormGroup>
                        <CenteredAlert color="warning">{t('reportsModals.noteMandatoryFields')}</CenteredAlert>
                    </React.Fragment>
                )}
            </FormModal>
            <RolesChecker roles={[Roles.admin, Roles.noc]}>
                <Button className="float-right" color="light" onClick={toggle}>
                    {t('reportsModals.addModalTitle')}
                </Button>
            </RolesChecker>
        </React.Fragment>
    );
};

export default enhance(ReportAddModal);
